import { useCallback, useEffect, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import debounce from 'lodash/debounce';
import { mixpanelTrack } from 'utils/app';
import { removeDiacritics } from 'utils/helpers';
import styled from 'styled-components';

const Wrapper = styled.div`
    flex: 1 1;
    display: flex;
    align-items: stretch;
    background-color: ${({ theme }) => theme.gray_7};
    border: 1px solid ${({ theme }) => theme.gray_5};
    border-radius: ${({ theme }) => theme.borderRadius_md};

    &:hover,
    &:focus-within {
        border-color: ${({ theme }) => theme.purple_4};
    }

    &:hover {
        background-color: ${({ theme }) => theme.purple_5};
    }

    &:focus-within {
        background-color: ${({ theme }) => theme.gray_7};
        border-color: ${({ theme }) => theme.purple_1};
    }
`;

const IconWrapper = styled.span`
    position: relative;
    align-self: center;
`;

const Icon = styled(FiSearch)`
    position: absolute;
    left: ${({ theme }) => theme.space_6}px;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.purple_1};
    font-size: 1.25em;
`;

const ICON_WIDTH = 42;

const Input = styled.input`
    all: unset;
    font: inherit;
    font-weight: ${({ theme }) => theme.fontMedium};
    background: transparent;
    border: 0;
    flex-grow: 1;
    padding: ${({ theme }) => `0 ${theme.space_6}px 0 ${ICON_WIDTH}px`};
    text-transform: capitalize;
    position: relative;
    max-width: 100%;
`;

function SearchInput({
    onSearchInputChange,
    name,
    defaultValue = '',
    ...props
}) {
    const debouncedOnChange = useCallback(
        debounce((value) => {
            onSearchInputChange(value);
            // Track search input usage for comparison with SmartSearch
            if (value) {
                mixpanelTrack('Lexical Search Input', {
                    query: value,
                });
            }
        }, 500),
        [onSearchInputChange],
    );
    const placeholder = `Search in ${name || 'category'}`;
    const [value, setValue] = useState(defaultValue);

    useEffect(() => {
        setValue(defaultValue);
    }, [defaultValue]);

    return (
        <Wrapper {...props}>
            <IconWrapper>
                <Icon />
            </IconWrapper>
            <Input
                aria-label={placeholder}
                placeholder={placeholder}
                type="search"
                value={value}
                onChange={(e) => {
                    setValue(e.target.value);
                    const value = removeDiacritics(e.target.value.trim());
                    if (!value) {
                        debouncedOnChange.cancel();
                        onSearchInputChange(value);
                    } else {
                        debouncedOnChange(value);
                    }
                }}
                onBlur={(e) => {
                    debouncedOnChange.cancel();
                    onSearchInputChange(
                        removeDiacritics(e.target.value.trim()),
                    );
                }}
            />
        </Wrapper>
    );
}

export default SearchInput;
